import React, { useRef } from 'react';
import { Row, Col, Image } from "react-bootstrap";
import Container from '../components/Container';
import SectionHeader from '../components/SectionHeader';
import Card from '../components/Card';
import { Leaf, Target, Recycle, Heart, Globe, Award } from 'lucide-react';
import { motion, useInView, useAnimation } from 'framer-motion';
import '../styles/components.css';

/* Images thématiques (développement durable, valorisation, éducation) */
const IMG_BANNER = "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=1200&h=400&fit=crop&q=80";
const IMG_VALEURS = "https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=600&h=400&fit=crop&q=80";
const IMG_EDUC4US = "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&h=400&fit=crop&q=80";

const Mission = () => {
  const engagements = [
    {
      icon: Leaf,
      title: "Développement durable",
      text: "Intégration du développement durable au cœur de nos activités pour rendre l'ensemble de nos actions le plus respectueux possible de l'environnement.",
      image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&h=400&fit=crop&q=80"
    },
    {
      icon: Target,
      title: "Réduction des impacts",
      text: "Accompagnement dans l'élaboration et la mise en œuvre de votre stratégie de maîtrise et de réduction de vos impacts environnementaux.",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop&q=80"
    },
    {
      icon: Recycle,
      title: "Valorisation optimale",
      text: "Optimisation de la gestion et du tri de vos biens et déchets pour maximiser leur valorisation et minimiser leur impact.",
      image: "https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=600&h=400&fit=crop&q=80"
    },
    {
      icon: Heart,
      title: "Société à mission",
      text: "Nous sommes une société à mission engagée dans la transition écologique et sociale, avec des objectifs mesurables et vérifiables.",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop&q=80"
    },
    {
      icon: Globe,
      title: "Action internationale",
      text: "Contribution au développement durable à l'international, notamment à travers notre programme educ4us pour l'accès à l'éducation.",
      image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&h=400&fit=crop&q=80"
    },
    {
      icon: Award,
      title: "Excellence opérationnelle",
      text: "Respect de nos engagements et de nos valeurs dans l'exercice de notre mission pour renforcer votre confiance.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop&q=80"
    }
  ];

  const introRef = useRef(null);
  const engagementsRef = useRef(null);
  const educ4usRef = useRef(null);

  const introInView = useInView(introRef, { once: true, margin: "-100px" });
  const engagementsInView = useInView(engagementsRef, { once: true, margin: "-100px" });
  const educ4usInView = useInView(educ4usRef, { once: true, margin: "-100px" });

  const introControls = useAnimation();
  const engagementsControls = useAnimation();
  const educ4usControls = useAnimation();

  React.useEffect(() => {
    if (introInView) introControls.start("visible");
  }, [introInView, introControls]);

  React.useEffect(() => {
    if (engagementsInView) engagementsControls.start("visible");
  }, [engagementsInView, engagementsControls]);

  React.useEffect(() => {
    if (educ4usInView) educ4usControls.start("visible");
  }, [educ4usInView, educ4usControls]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <div className="section" style={{ overflow: 'hidden' }}>
      <div
        role="img"
        aria-label="Engagement développement durable"
        style={{
          position: 'relative',
          width: '100%',
          height: '50vh',
          minHeight: 280,
          backgroundImage: `url(${IMG_BANNER})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 0
          }}
        />
        <Container style={{ position: 'relative', zIndex: 1, width: '100%' }}>
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center', color: '#fff' }}
          >
            <SectionHeader
              className="mission-hero-header"
              title="Notre engagement"
              subtitle="Des valeurs fortes au service de l'environnement et du développement durable"
            />
          </motion.div>
        </Container>
      </div>

      <Container>
        <motion.div
          ref={introRef}
          initial="hidden"
          animate={introControls}
          variants={fadeInUp}
        >
          <Row style={{ marginTop: 'var(--spacing-4xl)', marginBottom: 'var(--spacing-4xl)' }} className="g-4 align-items-center">
            <Col md={5}>
              <motion.div variants={fadeInUp} style={{ borderRadius: 'var(--border-radius-xl)', overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
                <Image src={IMG_VALEURS} alt="Nos valeurs" fluid style={{ width: '100%', objectFit: 'cover' }} />
              </motion.div>
            </Col>
            <Col md={7}>
              <span style={{
                display: 'inline-block',
                width: 48,
                height: 4,
                background: 'linear-gradient(90deg, var(--color-primary), var(--color-secondary))',
                borderRadius: 2,
                marginBottom: 'var(--spacing-lg)'
              }} />
              <h2 style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: 'var(--color-text)',
                marginBottom: 'var(--spacing-lg)'
              }}>
                Nos valeurs
              </h2>
              <p style={{
                fontSize: 'var(--font-size-lg)',
                lineHeight: 'var(--line-height-relaxed)',
                color: 'var(--color-text-light)',
                margin: 0
              }}>
                En intégrant le développement durable au cœur de nos activités, nous souhaitons rendre
                l'ensemble de nos activités le plus respectueux de l'environnement et vous aider vous aussi
                à limiter vos impacts environnementaux. Nous sommes une société à mission et nous vous
                accompagnons dans l'élaboration et la mise en œuvre de votre stratégie de maîtrise et de
                réduction de vos impacts environnementaux.
              </p>
            </Col>
          </Row>
        </motion.div>

        <motion.div
          ref={engagementsRef}
          initial="hidden"
          animate={engagementsControls}
          variants={staggerContainer}
          className="grid-services"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}
        >
          {engagements.map((engagement, index) => {
            const Icon = engagement.icon;
            return (
              <motion.div
                key={index}
                variants={staggerItem}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <Card
                  icon={Icon}
                  title={engagement.title}
                  text={engagement.text}
                  image={engagement.image}
                  imageAlt={engagement.title}
                />
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          ref={educ4usRef}
          initial="hidden"
          animate={educ4usControls}
          variants={fadeInUp}
        >
          <Row style={{ marginTop: 'var(--spacing-4xl)' }} className="g-4 align-items-center">
            <Col lg={5} className="order-2 order-lg-2">
              <div style={{ borderRadius: 'var(--border-radius-xl)', overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
                <img
                  src={IMG_EDUC4US}
                  alt="Programme educ4us - Éducation pour tous"
                  style={{ width: '100%', height: '260px', objectFit: 'cover' }}
                />
              </div>
            </Col>
            <Col lg={7} className="order-1 order-lg-1">
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--spacing-lg)' }}>
                <span style={{
                  width: 48,
                  height: 4,
                  background: 'linear-gradient(90deg, var(--color-secondary), var(--color-primary))',
                  borderRadius: 2
                }} />
              </div>
              <h2 style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: 'var(--color-primary)',
                marginBottom: 'var(--spacing-lg)'
              }}>
                Programme educ4us
              </h2>
              <p style={{
                fontSize: 'var(--font-size-lg)',
                lineHeight: 'var(--line-height-relaxed)',
                color: 'var(--color-text-light)',
                margin: 0
              }}>
                Vous pouvez contribuer à notre engagement en faveur de l'éducation pour tous à travers
                notre programme <strong style={{ color: 'var(--color-primary)' }}>educ4us</strong> qui
                construit des centres multimédia dans des pays en développement, contribuant ainsi au
                quatrième pilier du développement durable qui est l'accès à l'éducation pour tous.
              </p>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </div>
  );
};

export default Mission;
